import { useState, useMemo, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import SchemaOrg from '@/components/feature/SchemaOrg';
import { articles } from '@/mocks/articles';
import { categories, regionTabs, typeTabs } from '@/mocks/categories';

export default function CategoryPage() {
  const { categoryId } = useParams<{ categoryId: string }>();
  const [activeRegion, setActiveRegion] = useState('all');
  const [activeType, setActiveType] = useState('all');
  const [sortBy, setSortBy] = useState<'latest' | 'popular'>('latest');

  const currentCategory = categories.find((c) => c.slug === categoryId);

  useEffect(() => {
    if (currentCategory) {
      setActiveRegion(currentCategory.region === '欧美' ? 'oumei' : currentCategory.region === '日本' ? 'japan' : 'korea');
      setActiveType(currentCategory.type === '电影' ? 'movies' : currentCategory.type === '剧集' ? 'tv' : 'music');
    }
  }, [categoryId, currentCategory]);

  const filteredArticles = useMemo(() => {
    let result = [...articles];

    if (categoryId && categoryId !== 'all') {
      const cat = categories.find((c) => c.slug === categoryId);
      if (cat) {
        result = result.filter((a) => a.categoryId === cat.id);
      }
    } else {
      if (activeRegion !== 'all') {
        const regionMap: Record<string, string> = { oumei: '欧美', japan: '日本', korea: '韩国' };
        result = result.filter((a) => a.region === regionMap[activeRegion]);
      }
      if (activeType !== 'all') {
        const typeMap: Record<string, string> = { movies: '电影', tv: '剧集', music: '音乐' };
        result = result.filter((a) => a.type === typeMap[activeType]);
      }
    }

    result.sort((a, b) => {
      if (sortBy === 'popular') return b.views - a.views;
      return new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime();
    });

    return result;
  }, [categoryId, activeRegion, activeType, sortBy]);

  const pageTitle = currentCategory
    ? `${currentCategory.name} - 欧美日韩一区精选内容聚合平台`
    : '分类导航 - 欧美日韩一区精选内容聚合平台';

  const breadcrumbItems = currentCategory
    ? [
        { label: '首页', href: '/' },
        { label: '分类导航', href: '/category/all' },
        { label: currentCategory.name },
      ]
    : [{ label: '首页', href: '/' }, { label: '分类导航' }];

  return (
    <>
      <SchemaOrg
        type="CollectionPage"
        data={{
          name: pageTitle,
          description: currentCategory?.description || '欧美日韩一区全部分类资源导航',
          url: `https://www.oumeirihan.com/category/${categoryId || 'all'}`,
        }}
      />

      <Navbar />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb items={breadcrumbItems} />

          <div className="mt-6 mb-10">
            <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900">
              {currentCategory ? currentCategory.name : '欧美日韩一区分类导航'}
            </h1>
            {currentCategory && (
              <p className="text-sm text-foreground-500 mt-2">{currentCategory.description}</p>
            )}
          </div>

          {/* Filter Tabs */}
          <div className="mb-8 space-y-4">
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-sm font-medium text-foreground-700 whitespace-nowrap">地区：</span>
              <div className="flex items-center gap-1.5 bg-background-100 rounded-full p-1">
                {regionTabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveRegion(tab.id)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                      activeRegion === tab.id
                        ? 'bg-primary-500 text-background-50'
                        : 'text-foreground-600 hover:text-foreground-900 hover:bg-background-200'
                    }`}
                  >
                    {tab.name}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-sm font-medium text-foreground-700 whitespace-nowrap">类型：</span>
              <div className="flex items-center gap-1.5 bg-background-100 rounded-full p-1">
                {typeTabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveType(tab.id)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                      activeType === tab.id
                        ? 'bg-primary-500 text-background-50'
                        : 'text-foreground-600 hover:text-foreground-900 hover:bg-background-200'
                    }`}
                  >
                    {tab.name}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-foreground-700 whitespace-nowrap">排序：</span>
              <div className="flex items-center gap-1.5">
                {[
                  { id: 'latest', name: '最新发布' },
                  { id: 'popular', name: '最多阅读' },
                ].map((s) => (
                  <button
                    key={s.id}
                    onClick={() => setSortBy(s.id as 'latest' | 'popular')}
                    className={`px-4 py-1.5 rounded-lg text-xs font-medium transition-all duration-200 whitespace-nowrap cursor-pointer border ${
                      sortBy === s.id
                        ? 'bg-primary-50 text-primary-700 border-primary-200'
                        : 'text-foreground-500 border-background-200 hover:border-foreground-300'
                    }`}
                  >
                    {s.name}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Article Grid */}
          {filteredArticles.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 pb-16">
              {filteredArticles.map((article) => (
                <ArticleCard key={article.id} article={article} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
                <i className="ri-inbox-line text-2xl"></i>
              </div>
              <p className="text-foreground-500">当前筛选条件下暂无内容</p>
              <button
                onClick={() => { setActiveRegion('all'); setActiveType('all'); }}
                className="mt-4 text-sm text-primary-500 hover:text-primary-600 font-medium cursor-pointer"
              >
                清除筛选条件
              </button>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}